import { Clipboard } from '@angular/cdk/clipboard';
import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { CognitoService } from 'src/app/auth/services/cognito.service';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-setup-mfa',
  templateUrl: './setup-mfa.component.html',
  styleUrls: ['./setup-mfa.component.sass']
})
export class SetupMfaComponent {

  /** URI `otpauth://` que se renderiza como QR. */
  qrData = '';
  /** Secret en crudo — alternativa al QR para entrada manual. */
  secret = '';
  step = 1;
  code = '';

  /**
   * Motivo por el que no hay QR ni clave para mostrar.
   *
   * Sin esto el modal se abría con el recuadro del QR vacío y ni una palabra:
   * `generateQRCode()` se llama desde el constructor y su promesa rechazada no
   * la atendía nadie. El cliente veía un blanco y concluía que no lo dejaban.
   */
  errorQr: string | null = null;

  constructor(
    public dialogRef: MatDialogRef<SetupMfaComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private cognito: CognitoService,
    private clipboard: Clipboard,
  ) {
    this.generateQRCode();
  }

  onNoClick(): void {
    this.dialogRef.close();
  }

  async generateQRCode() {
    this.errorQr = null;
    try {
      const { uri, secret } = await this.cognito.generateQRCode();
      this.qrData = uri;
      this.secret = secret;
    } catch (error) {
      this.errorQr =
        'No pudimos generar el código de configuración. Cerrá esta ventana y ' +
        'volvé a intentarlo; si sigue igual, escribinos a soporte.';
    }
  }

  /** Reintento del QR sin tener que cerrar y reabrir el modal. */
  async reintentarQr(): Promise<void> {
    await this.generateQRCode();
  }

  /** Secret agrupado de 4 en 4 para que sea fácil de tipear manualmente. */
  get secretFormatted(): string {
    if (!this.secret) return '';
    return this.secret.replace(/(.{4})/g, '$1 ').trim();
  }

  copySecret(): void {
    if (!this.secret) return;
    this.clipboard.copy(this.secret);
    Swal.fire({
      toast: true,
      position: 'top-end',
      icon: 'success',
      title: 'Clave copiada al portapapeles',
      showConfirmButton: false,
      timer: 2000,
      timerProgressBar: true,
    });
  }

  async nextStep(step: number) {
    if (step === 1) {
      this.step = 2;
      return;
    }

    this.code = this.code.replace(/\s/g, '');

    let result: any;
    try {
      result = await this.cognito.verifySoftwareToken(this.code);
    } catch (error) {
      // Cognito LANZA para el código inválido y para `LimitExceededException`
      // tras varios intentos seguidos; no devuelve un `Status` distinto de
      // SUCCESS. Sin este `catch` la promesa reventaba acá y el `if` de abajo
      // no llegaba a correr nunca: ni "código incorrecto" ni nada.
      Swal.fire({
        icon: 'error',
        title: 'No se pudo verificar',
        text:
          'El código no es correcto, o hubo demasiados intentos seguidos. ' +
          'Esperá un momento y probá con el código nuevo de tu Authenticator.',
        confirmButtonColor: '#1e5a3f',
      });
      return;
    }

    if (!result || result.Status !== 'SUCCESS') {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'El código ingresado no es correcto',
      });
      return;
    }

    // Importante: AWAIT a `enableMFA()` antes de cerrar el modal. Sin
    // await, `cognito.getMFAStatus()` del padre puede ejecutarse mientras
    // Cognito todavía no aplicó `setPreferredMFA('TOTP')` y devolver
    // `NOMFA`, dejando el toggle del perfil en estado incorrecto.
    try {
      await this.cognito.enableMFA();
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'No se pudo activar MFA',
        text:
          'El código fue verificado pero falló la activación en Cognito. Reintenta nuevamente.',
        confirmButtonColor: '#1e5a3f',
      });
      return;
    }

    await Swal.fire({
      icon: 'success',
      title: 'MFA activado',
      text: 'La autenticación multifactor quedó configurada correctamente.',
      confirmButtonColor: '#1e5a3f',
      timer: 2500,
      timerProgressBar: true,
    });

    // Señal al padre: refrescar status MFA y data del usuario completa.
    this.dialogRef.close(true);
  }

  validateNumberInput(event: KeyboardEvent): void {
    const allowedKeys = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'];
    if (!allowedKeys.includes(event.key)) {
      event.preventDefault();
    }
  }

}
